import { ImageCard } from 'src/types/models/ImageCard';
import { useContext, useMemo } from 'react';
import {
  Message,
  MessageSkill,
  RightPanelSections,
  TaskState,
} from 'src/types';
import './ThreadImageGenerationCard.scss';
import {
  useLeftPanelActions,
  useDeleteAttachment,
  useRightSidePanelActions,
  useConversationImages,
  useImageMaskingEditClick,
  useImageMaskingPanelActions,
  useUpdateImagesForEditor,
  useGetUser,
  useBreakpoint,
} from 'src/hooks';
import { useCollapse } from 'react-collapsed';
import { ImageGenModifyPanel } from 'src/components/FlatAppearance/components/ImageGenModifyPanel';
import { EditImageUpsell } from 'src/v2/components/ThreadMessage/components/EditImageUpsell';
import { FeedbackRating } from 'src/v2/components/ThreadMessage/components/FeedbackRating';
import { ThreadMediaThumbnail } from 'src/v2/components/ThreadMessage/components/ThreadMediaThumbnail';
import { LearnMoreLink } from 'src/v2/commonComponents/LearnMoreLink';
import { IMAGE_EDITOR, IMAGE_GENERATION } from 'src/constants/externalLinks';
import { ThreadMediaFooter } from 'src/v2/components/ThreadMessage/components/ThreadMediaFooter';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { ThreadMediaSkeleton } from 'src/v2/components/ThreadMessage/components/ThreadMediaSkeleton';
import { RelatedQuestions } from 'src/components/RelatedQuestions';
import { useChatHistory } from 'src/hooks';
interface ThreadImageGenerationCardProps {
  message: Message;
  messageId: string;
  imageCard: ImageCard;
  messageOriginalQuery?: string;
  messageCleanQuery?: string;
  isStreaming?: boolean;
  taskState?: TaskState;
}

const DEFAULT_IMAGE_INDEX = 0;

export const ThreadImageGenerationCard = ({
  message,
  messageId,
  imageCard,
  messageOriginalQuery,
  messageCleanQuery,
  isStreaming,
  taskState,
}: ThreadImageGenerationCardProps) => {
  const { isNinjaRetired } = useChatHistory();
  const { isOpenTier, isFreeTier } = useGetUser();
  const { isMobileOrTablet } = useBreakpoint();
  const { toggleLeftPanelExpanded } = useLeftPanelActions();
  const { watermarked_images } = imageCard;
  const { onDeleteAllAttachments } = useDeleteAttachment();
  const {
    onChangeImageMaskingRightPanelData,
    resetImageMaskingPanelInfo,
    onChangeCurrentImagesList,
    onChangeSelectedImageIndex,
    onChangeMessageId,
  } = useImageMaskingPanelActions();
  const { setRightSidePanelData } = useRightSidePanelActions();
  const imagesArray = useConversationImages(imageCard);
  const { enableEditingMode } = useImageMaskingEditClick();
  const { threadInputBoxRef } = useContext(ForwardRefContext);

  useUpdateImagesForEditor(imagesArray, messageId);

  const isLimitedEdited = useMemo(
    () => !!watermarked_images && (isOpenTier || isFreeTier),
    [watermarked_images, isOpenTier, isFreeTier],
  );

  const {
    isExpanded: isExpandedImageGenSettings,
    setExpanded,
    getCollapseProps,
  } = useCollapse({
    defaultExpanded: false,
  });

  const handleCloseImageGenSettings = () => {
    setExpanded(false);
  };

  const handleSetCurrentMedia = (index: number) => {
    resetImageMaskingPanelInfo();
    onDeleteAllAttachments();
    toggleLeftPanelExpanded(false);
    onChangeImageMaskingRightPanelData(imagesArray[index]);
    onChangeCurrentImagesList(imagesArray);
    onChangeSelectedImageIndex(index);
    setRightSidePanelData({
      panelType: RightPanelSections.IMAGE,
      isExpanded: true,
    });

    if (messageId) {
      onChangeMessageId(messageId);
    }
  };

  const handleCurrentMediaEditClick = () => {
    handleSetCurrentMedia(DEFAULT_IMAGE_INDEX);

    enableEditingMode({
      currentImageUrl: imagesArray[DEFAULT_IMAGE_INDEX].url,
    });
    if (!isMobileOrTablet) threadInputBoxRef?.current?.focus();
  };

  const dimensionDescription = useMemo(() => {
    //TODO(olha): it's a temporary workaround until BE sends proper dimension values
    const currentImage = imagesArray[0];
    if (!currentImage) {
      return '';
    }

    const { size } = currentImage;

    return size;
  }, [imagesArray]);

  return (
    <div className="nj-thread-research-media nj-thread-image-generation-media-wrapper">
      <div className="nj-thread-image-generation-header-wrapper">
        <h6 className="nj-thread-image-generation-header-title">
          <span>Image generation</span>
          <span className="nj-thread-image-generation-header-title--caption">
            {imagesArray ? dimensionDescription : ''}
          </span>
        </h6>
      </div>

      <div className="nj-thread-image-generation-media-wrapper--image-list">
        {imagesArray.length > 0 ? (
          imagesArray.map((item, index) => (
            <ThreadMediaThumbnail
              mediaType="image"
              key={item.id}
              thumbnail={item.url}
              title={item.title}
              onClick={() => handleSetCurrentMedia(index)}
              icon={item.icon || undefined}
              source={item.source}
              status={item.status}
              withDisabledOverlay={item.isDisabled}
              withWatermark={isLimitedEdited}
              size={item.size}
            />
          ))
        ) : (
          <div className="nj-thread-image-generation-media-wrapper--skeleton">
            <ThreadMediaSkeleton />
          </div>
        )}
      </div>

      {!isNinjaRetired && (
        <ThreadMediaFooter
          message={message}
          url={imagesArray?.[0]?.url}
          isEditDisabled={isLimitedEdited}
          onEditClick={handleCurrentMediaEditClick}
          isStreaming={isStreaming}
          isExpandedImageGenSettings={isExpandedImageGenSettings}
          setModifyPanelExpanded={setExpanded}
          taskState={taskState}
        />
      )}

      {!isNinjaRetired && (
        <div {...getCollapseProps()}>
          <ImageGenModifyPanel
            isOpen={isExpandedImageGenSettings}
            onClose={handleCloseImageGenSettings}
            messageOriginalQuery={messageOriginalQuery}
            messageCleanQuery={messageCleanQuery}
          />
        </div>
      )}

      {!isNinjaRetired && isLimitedEdited && <EditImageUpsell />}

      <RelatedQuestions
        message={message}
        title="Related queries"
        isStreaming={isStreaming}
      />

      <FeedbackRating
        title="Rate the images"
        message={message}
        isStreaming={!!isStreaming}
        operationType="message-level"
        messageSkill={MessageSkill.IMAGE_GENERATION}
      />

      {!!watermarked_images ? (
        <LearnMoreLink
          to={IMAGE_EDITOR}
          dataE2E="learn-more-image-editor-link"
          title="Learn more about image editing"
          className="nj-thread-image-generation--learn-more"
        />
      ) : (
        <LearnMoreLink
          to={IMAGE_GENERATION}
          dataE2E="learn-more-image-generation-link"
          title="Learn more about image generation"
          className="nj-thread-image-generation--learn-more"
        />
      )}
    </div>
  );
};
